"use client";

import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { XIcon } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

function Dialog({ ...props }: DialogPrimitive.Root.Props) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({ className, ...props }: DialogPrimitive.Backdrop.Props) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      className={cn(
        "data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 bg-black/80 duration-100 supports-backdrop-filter:backdrop-blur-xs fixed inset-0 isolate z-50",
        className
      )}
      {...props}
    />
  );
}

// Everything a popup is regardless of how big it is. The sizing groups — display, gap, padding,
// width, inset, translate, text — live only in the size classes below, so `cn()` never has to
// resolve a conflict between the two halves.
const dialogContentBase =
  "bg-background data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 ring-foreground/10 rounded-xl ring-1 duration-100 fixed z-50 w-full outline-none";

const dialogContentSizes = {
  default:
    "grid max-w-[calc(100%-2rem)] gap-4 p-4 text-xs/relaxed sm:max-w-sm top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
  // `h-dvh` and the safe-area padding are the sheet's alone: on a phone the browser's own bottom
  // bar overlays the viewport, so `100vh` puts the pinned footer underneath it and the last inch
  // of a document behind it. The dynamic unit tracks the bar and the inset clears what is left.
  // Both are switched off from 840px up, where the panel is centred and there is no bar.
  reading:
    "flex max-w-none flex-col gap-0 overflow-hidden p-0 pb-[env(safe-area-inset-bottom)] text-sm sm:max-w-none inset-0 top-0 left-0 h-dvh translate-x-0 translate-y-0 min-[840px]:inset-auto min-[840px]:top-1/2 min-[840px]:left-1/2 min-[840px]:h-auto min-[840px]:max-h-[calc(100vh-4rem)] min-[840px]:w-[1100px] min-[840px]:max-w-[calc(100vw-2rem)] min-[840px]:-translate-x-1/2 min-[840px]:-translate-y-1/2 min-[840px]:pb-0",
} as const;

type DialogContentSize = keyof typeof dialogContentSizes;

function DialogContent({
  className,
  children,
  showCloseButton = true,
  size = "default",
  ...props
}: DialogPrimitive.Popup.Props & {
  showCloseButton?: boolean;
  /**
   * `"reading"` is the shell for a document read in full — a transcript, a conflicted-changes
   * review, a file preview. Below 840px it is a full-screen sheet, sized to the dynamic viewport
   * and padded clear of the phone's bottom bar, because a centred card with a scrim around it
   * wastes the only width there is and a footer under the browser's own chrome is a footer nobody
   * can press; from 840px up it is a 1100px panel centred in the viewport and no taller than it.
   * It only lays out the shell: the caller pins its own header and footer and scrolls the body
   * between them, and gives the footer's buttons a 44px target on the sheet.
   */
  size?: DialogContentSize;
}) {
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Popup
        data-slot="dialog-content"
        className={cn(dialogContentBase, dialogContentSizes[size], className)}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            render={<Button variant="ghost" className="absolute top-2 right-2" size="icon-sm" />}
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Popup>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div data-slot="dialog-header" className={cn("gap-1 flex flex-col", className)} {...props} />
  );
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  showCloseButton?: boolean;
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className)}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close render={<Button variant="outline" />}>Close</DialogPrimitive.Close>
      )}
    </div>
  );
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("text-sm font-medium", className)}
      {...props}
    />
  );
}

function DialogDescription({ className, ...props }: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        "text-muted-foreground *:[a]:hover:text-foreground text-xs/relaxed *:[a]:underline *:[a]:underline-offset-3",
        className
      )}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
